import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Button, Card, Col, Empty, Form, Input, InputNumber, Modal, Select, Space,
    Row as AntRow, Spin, Table, Tabs, Tag, Typography, Pagination, message
} from 'antd';
import {
    ArrowLeftOutlined, EditOutlined, PlusOutlined, ReloadOutlined, RightOutlined,
    SettingOutlined, StopOutlined, DownloadOutlined, UploadOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import AgreementPriceMatrix from './AgreementPriceMatrix';
import {
    getBusinessAPI, getBusinessOrganizationsAPI, postBusinessOrganizationAPI,
    putBusinessOrganizationAPI, inactivateBusinessOrganizationAPI,
    getBusinessAgreementsAPI, postBusinessAgreementAPI, putBusinessAgreementAPI,
    getCoveragePlansAPI, postCoveragePlanAPI, putCoveragePlanAPI,
    getProductsCoverageAPI, postProductsCoverageAPI, putProductsCoverageAPI,
    getGlobalProductsCoverageAPI, postGlobalProductsCoverageAPI, putGlobalProductsCoverageAPI,
    importProductPriceListAPI,
    getServicesCoverageAPI, postServiceCoverageAPI, putServiceCoverageAPI,
    getGlobalServicesCoverageAPI, postGlobalServiceCoverageAPI, putGlobalServiceCoverageAPI,
    getOrganizationManagementProductsAPI, postOrganizationManagementProductAPI,
    postOrganizationLocalProductAPI, putOrganizationManagementProductAPI,
    getOrganizationCatalogProductsAPI, getOrganizationClinicalServicesAPI,
    getOrganizationClientDataAPI, getProcedureConfigurationsAPI,
    postProcedureConfigurationAPI, putProcedureConfigurationAPI,
    getOrganizationProcedureDocumentsAPI, getProcedureDocumentAssignmentsAPI,
    postProcedureDocumentAssignmentAPI, putProcedureDocumentAssignmentAPI,
    deleteProcedureDocumentAssignmentAPI
} from '../store/user/restCallUser';

type Row = Record<string, any>;
type Kind = 'business' | 'agreement' | 'plan' | 'product' | 'service';

const valueOf = (row: Row, ...names: string[]) => {
    for (const name of names) if (row?.[name] !== undefined && row?.[name] !== null) return row[name];
    return undefined;
};
const unwrapResponse = (response: any): any => {
    let value = response?.Response ?? response?.data ?? response;
    for (let attempt = 0; attempt < 3; attempt += 1) {
        if (typeof value === 'string') {
            try { value = JSON.parse(value); continue; } catch { return value; }
        }
        if (value && !Array.isArray(value) && typeof value === 'object') {
            if (value.data !== undefined) { value = value.data; continue; }
            if (value.Response !== undefined) { value = value.Response; continue; }
        }
        break;
    }
    return value;
};
const rowsOf = (response: any): Row[] => {
    const value = unwrapResponse(response);
    return Array.isArray(value) ? value : [];
};
const pageOf = (response: any): { items: Row[]; total: number; page: number; pageSize: number } => {
    const value = unwrapResponse(response);
    if (Array.isArray(value)) return { items: value, total: value.length, page: 1, pageSize: value.length || 25 };
    const items = Array.isArray(value?.items) ? value.items : Array.isArray(value?.Items) ? value.Items : [];
    return {
        items,
        total: Number(value?.total ?? value?.Total ?? value?.count ?? value?.Count ?? items.length),
        page: Number(value?.page ?? value?.Page ?? 1),
        pageSize: Number(value?.pageSize ?? value?.PageSize ?? (items.length || 25))
    };
};
const objectOf = (response: any): Row => {
    const value = unwrapResponse(response);
    return value && !Array.isArray(value) && typeof value === 'object' ? value : {};
};
const responseError = (response: any): string | null => {
    if (!response || response.Error !== null || response.StatusCode < 200 || response.StatusCode >= 300) {
        return response?.Error?.message || response?.Error || 'No se pudo completar la operación.';
    }
    return null;
};
const idOf = (row: Row) => String(valueOf(row, 'id', 'Id') || '');
const labelOf = (row: Row, ...names: string[]) => String(valueOf(row, ...names) || '');
const isProcedureService = (row: Row | undefined) => {
    const category = String(valueOf(row || {}, 'category', 'Category') || '').trim().toUpperCase();
    const name = String(valueOf(row || {}, 'name', 'Name', 'serviceName', 'ServiceName') || '').trim().toUpperCase();
    return category === 'PROCEDIMIENTOS' || name === 'PROCEDIMIENTOS' || name.includes('PROCEDIMIENTO');
};
const isProcedureProduct = (row: Row | undefined) => {
    const category = String(valueOf(row || {}, 'serviceCategory', 'ServiceCategory', 'category', 'Category') || '').trim().toUpperCase();
    const service = String(valueOf(row || {}, 'service', 'Service', 'serviceName', 'ServiceName') || '').trim().toUpperCase();
    return category === 'PROCEDIMIENTOS' || service === 'PROCEDIMIENTOS' || service.includes('PROCEDIMIENTO');
};
const isConsentDocument = (row: Row | undefined) => {
    const code = String(valueOf(row || {}, 'typeDocumentCode', 'TypeDocumentCode', 'code', 'Code') || '')
        .trim()
        .toUpperCase()
        .replace(/[- ]/g, '_');
    const name = String(valueOf(row || {}, 'typeDocumentName', 'TypeDocumentName', 'documentTypeName', 'DocumentTypeName', 'name', 'Name') || '')
        .trim()
        .toUpperCase();
    return code === 'CONSENTIMIENTO_INFORMADO'
        || code === 'CONSENTIMIENTO'
        || name.includes('CONSENTIMIENTO INFORMADO');
};

interface Props { organizationId: string; }

export default function OrganizationBusinessCoverageManagement({ organizationId }: Props) {
    const [catalogue, setCatalogue] = useState<Row[]>([]);
    const [businesses, setBusinesses] = useState<Row[]>([]);
    const [organizationData, setOrganizationData] = useState<Row>({});
    const [agreements, setAgreements] = useState<Row[]>([]);
    const [plans, setPlans] = useState<Row[]>([]);
    const [productCoverage, setProductCoverage] = useState<Row[]>([]);
    const [serviceCoverage, setServiceCoverage] = useState<Row[]>([]);
    const [globalProductCoverage, setGlobalProductCoverage] = useState<Row[]>([]);
    const [globalProductPage, setGlobalProductPage] = useState(1);
    const [globalProductTotal, setGlobalProductTotal] = useState(0);
    const [globalProductLoading, setGlobalProductLoading] = useState(false);
    const [globalProductSearch, setGlobalProductSearch] = useState('');
    const [globalProductServiceId, setGlobalProductServiceId] = useState('');
    const [globalServiceCoverage, setGlobalServiceCoverage] = useState<Row[]>([]);
    const [products, setProducts] = useState<Row[]>([]);
    const [services, setServices] = useState<Row[]>([]);
    const [catalogProducts, setCatalogProducts] = useState<Row[]>([]);
    const [catalogPage, setCatalogPage] = useState(1);
    const [catalogTotal, setCatalogTotal] = useState(0);
    const [catalogLoading, setCatalogLoading] = useState(false);
    const catalogPageSize = 25;
    const [procedureConfigurations, setProcedureConfigurations] = useState<Row[]>([]);
    const [procedureDocuments, setProcedureDocuments] = useState<Row[]>([]);
    const [procedureAssignments, setProcedureAssignments] = useState<Row[]>([]);
    const [procedureAssignmentsLoaded, setProcedureAssignmentsLoaded] = useState(false);
    const [selectedBusinessId, setSelectedBusinessId] = useState('');
    const [selectedAgreementId, setSelectedAgreementId] = useState('');
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [navigationLevel, setNavigationLevel] = useState<'businesses' | 'agreements' | 'plans' | 'coverage' | 'matrix'>('businesses');
    const [coverageTab, setCoverageTab] = useState('products');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [kind, setKind] = useState<Kind | null>(null);
    const [editing, setEditing] = useState<Row | null>(null);
    const [businessType, setBusinessType] = useState('');
    // Filtro opcional para localizar productos por su servicio de str.services.
    // El producto conserva su ServiceId del catálogo; no se envía este filtro
    // en los payloads de cobertura.
    const [productServiceFilter, setProductServiceFilter] = useState('');
    const [productSource, setProductSource] = useState<'CATALOG' | 'LOCAL'>('CATALOG');
    const [catalogSearch, setCatalogSearch] = useState('');
    const [selectedProductIsProcedure, setSelectedProductIsProcedure] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkRows, setBulkRows] = useState<Row[]>([]);
    const [bulkFileName, setBulkFileName] = useState('');
    const [bulkResult, setBulkResult] = useState<Row | null>(null);
    const [bulkSubmitting, setBulkSubmitting] = useState(false);
    const [form] = Form.useForm();
    const globalProductPageSize = 25;

    const run = async (call: Promise<any>, success?: string) => {
        const response = await call;
        const failure = responseError(response);
        if (failure) throw new Error(String(failure));
        if (success) message.success(success);
        return rowsOf(response);
    };

    const ownBusiness = useMemo(
        () => businesses.find(row => String(valueOf(row, 'type', 'Type') || '').toUpperCase() === 'PROPIA'),
        [businesses]
    );
    const ownBusinessActive = useMemo(
        () => String(valueOf(ownBusiness || {}, 'status', 'Status') || '').toUpperCase() === 'ACTIVO',
        [ownBusiness]
    );
    const canAddBusiness = !ownBusiness || ownBusinessActive;
    const organizationValue = (...names: string[]) => valueOf(organizationData, ...names);
    const ownFormValues = useCallback(() => ({
        sourceType: 'LOCAL',
        businessId: undefined,
        type: 'PROPIA',
        ruc: valueOf(organizationData, 'ruc', 'Ruc'),
        name: valueOf(organizationData, 'name', 'Name', 'legalName', 'LegalName'),
        address: valueOf(organizationData, 'address', 'Address'),
        phone: valueOf(organizationData, 'phone', 'Phone'),
        emailLegalRepresentative: valueOf(organizationData,
            'email', 'Email', 'emailLegalRepresentative', 'EmailLegalRepresentative'
        )
    }), [organizationData]);

    const loadBusinesses = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const [catalogueResponse, businessesResponse, productsResponse, servicesResponse] = await Promise.all([
                getBusinessAPI(), getBusinessOrganizationsAPI(organizationId),
                getOrganizationManagementProductsAPI(organizationId, undefined, false), getOrganizationClinicalServicesAPI(organizationId)
            ]);
            const loadFailure = [catalogueResponse, businessesResponse, productsResponse, servicesResponse]
                .map(responseError)
                .find(Boolean);
            if (loadFailure) throw new Error(String(loadFailure));
            let organizationDataResponse: any = null;
            try { organizationDataResponse = await getOrganizationClientDataAPI(organizationId); }
            catch { organizationDataResponse = null; }
            setCatalogue(rowsOf(catalogueResponse));
            setBusinesses(rowsOf(businessesResponse));
            setProducts(rowsOf(productsResponse));
            setServices(rowsOf(servicesResponse));
            // La configuración pdt.procedure pertenece al producto de la
            // organización. Se carga aquí para que el mismo formulario pueda
            // editarla, sin obligar a navegar a otro módulo.
            try {
                const procedureResponse = await getProcedureConfigurationsAPI(organizationId);
                const procedureFailure = responseError(procedureResponse);
                if (!procedureFailure) setProcedureConfigurations(rowsOf(procedureResponse));
            } catch {
                // Una versión anterior del gateway puede no exponer todavía
                // este endpoint; el registro comercial sigue funcionando.
            }
            try {
                const documentResponse = await getOrganizationProcedureDocumentsAPI(organizationId, 'ACTIVO');
                const documentFailure = responseError(documentResponse);
                if (!documentFailure) setProcedureDocuments(rowsOf(documentResponse));
            } catch {
                // Los documentos son opcionales; no bloquean el registro del producto.
            }
            const clientData = objectOf(organizationDataResponse);
            setOrganizationData(clientData);
        } catch (e: any) { setError(e?.message || 'No se pudo cargar la configuración.'); }
        finally { setLoading(false); }
    }, [organizationId]);

    const loadCatalogProducts = useCallback(async (serviceId?: string, search = '', page = 1) => {
        if (!serviceId) {
            setCatalogProducts([]);
            setCatalogTotal(0);
            setCatalogPage(1);
            return;
        }
        setCatalogLoading(true);
        try {
            const response = await getOrganizationCatalogProductsAPI(
                organizationId,
                serviceId,
                search,
                page,
                catalogPageSize
            );
            const failure = responseError(response);
            if (failure) throw new Error(String(failure));
            const paged = pageOf(response);
            setCatalogProducts(paged.items);
            setCatalogTotal(paged.total);
            setCatalogPage(paged.page || page);
        } catch (e: any) {
            setCatalogProducts([]);
            setCatalogTotal(0);
            setError(e?.message || 'No se pudo cargar el catálogo global.');
        } finally {
            setCatalogLoading(false);
        }
    }, [organizationId]);

    const loadAgreements = useCallback(async (businessId: string) => {
        if (!businessId) { setAgreements([]); return; }
        try { setAgreements(await run(getBusinessAgreementsAPI(organizationId, businessId))); }
        catch (e: any) { setError(e?.message || 'No se pudieron cargar los convenios.'); }
    }, [organizationId]);
    const loadPlans = useCallback(async (groupId: string) => {
        if (!groupId) { setPlans([]); return; }
        try { setPlans(await run(getCoveragePlansAPI(organizationId, groupId))); }
        catch (e: any) { setError(e?.message || 'No se pudieron cargar los planes.'); }
    }, [organizationId]);
    const loadGlobalProductCoverage = useCallback(async (
        agreementId: string,
        page = 1,
        serviceId = '',
        search = ''
    ) => {
        if (!agreementId) {
            setGlobalProductCoverage([]);
            setGlobalProductTotal(0);
            setGlobalProductPage(1);
            return;
        }
        setGlobalProductLoading(true);
        try {
            const response = await getGlobalProductsCoverageAPI(
                organizationId,
                agreementId,
                serviceId || undefined,
                search,
                page,
                globalProductPageSize
            );
            const failure = responseError(response);
            if (failure) throw new Error(String(failure));
            const paged = pageOf(response);
            setGlobalProductCoverage(paged.items);
            setGlobalProductTotal(paged.total);
            setGlobalProductPage(paged.page || page);
        } catch (e: any) {
            setGlobalProductCoverage([]);
            setGlobalProductTotal(0);
            setError(e?.message || 'No se pudo cargar la cobertura global de productos.');
        } finally {
            setGlobalProductLoading(false);
        }
    }, [organizationId]);
    const loadGlobalCoverage = useCallback(async (agreementId: string) => {
        if (!agreementId) { setGlobalServiceCoverage([]); return; }
        try {
            const response = await getGlobalServicesCoverageAPI(organizationId, agreementId);
            const failure = responseError(response);
            if (failure) throw new Error(String(failure));
            setGlobalServiceCoverage(rowsOf(response));
        } catch (e: any) { setError(e?.message || 'No se pudo cargar la cobertura global del convenio.'); }
    }, [organizationId]);
    const loadCoverage = useCallback(async (planId: string) => {
        if (!planId) { setProductCoverage([]); setServiceCoverage([]); return; }
        try {
            const [productResponse, serviceResponse] = await Promise.all([
                getProductsCoverageAPI(organizationId, planId), getServicesCoverageAPI(organizationId, planId)
            ]);
            const productFailure = responseError(productResponse); const serviceFailure = responseError(serviceResponse);
            if (productFailure) throw new Error(String(productFailure));
            if (serviceFailure) throw new Error(String(serviceFailure));
            setProductCoverage(rowsOf(productResponse)); setServiceCoverage(rowsOf(serviceResponse));
        } catch (e: any) { setError(e?.message || 'No se pudieron cargar las coberturas.'); }
    }, [organizationId]);
    const loadProcedureAssignments = useCallback(async (procedureId: string) => {
        if (!procedureId) {
            setProcedureAssignments([]);
            setProcedureAssignmentsLoaded(true);
            return [];
        }
        setProcedureAssignmentsLoaded(false);
        try {
            const response = await getProcedureDocumentAssignmentsAPI(organizationId, procedureId);
            const failure = responseError(response);
            if (failure) throw new Error(String(failure));
            const rows = rowsOf(response);
            setProcedureAssignments(rows);
            setProcedureAssignmentsLoaded(true);
            return rows;
        } catch (e: any) {
            setProcedureAssignments([]);
            setProcedureAssignmentsLoaded(true);
            message.warning(e?.message || 'No se pudieron cargar los documentos del procedimiento.');
            return [];
        }
    }, [organizationId]);

    useEffect(() => { void loadBusinesses(); }, [loadBusinesses]);
    useEffect(() => { setSelectedAgreementId(''); setSelectedGroupId(''); setSelectedPlanId(''); void loadAgreements(selectedBusinessId); }, [selectedBusinessId, loadAgreements]);
    useEffect(() => {
        setSelectedGroupId(selectedAgreementId);
        setSelectedPlanId('');
        setGlobalProductPage(1);
        setGlobalProductServiceId('');
        setGlobalProductSearch('');
        void loadPlans(selectedAgreementId);
        void loadGlobalCoverage(selectedAgreementId);
    }, [selectedAgreementId, loadPlans, loadGlobalCoverage]);
    useEffect(() => {
        void loadGlobalProductCoverage(
            selectedAgreementId,
            globalProductPage,
            globalProductServiceId,
            globalProductSearch
        );
    }, [selectedAgreementId, globalProductPage, globalProductServiceId, globalProductSearch, loadGlobalProductCoverage]);
    useEffect(() => { void loadCoverage(selectedPlanId); }, [selectedPlanId, loadCoverage]);
    useEffect(() => {
        if (kind === 'business' && businessType === 'PROPIA') {
            form.setFieldsValue(ownFormValues());
        }
    }, [kind, businessType, form, ownFormValues]);

    const open = (nextKind: Kind, row?: Row) => {
        if (nextKind === 'business' && !row && ownBusiness && !ownBusinessActive) {
            message.warning('La empresa PROPIA esta inactiva. Reactivela antes de asociar empresas externas.');
            return;
        }
        setKind(nextKind); setEditing(row || null);
        const initialBusinessType = !row && nextKind === 'business'
            ? (ownBusiness ? 'ASEGURADORA' : 'PROPIA')
            : undefined;
        const initial = row ? { ...row } : {
            status: nextKind === 'agreement' ? 'BORRADOR' : 'ACTIVO', sourceType: 'CATALOG',
            type: nextKind === 'business' ? initialBusinessType : 'ASEGURADORA', coverageType: 'PORCENTAJE', coverageValue: 0,
            copaymentType: 'PORCENTAJE', copaymentValue: 100, price: 0
        };
        if (nextKind === 'business') {
            const initialType = String(valueOf(initial, 'type', 'Type') || '').toUpperCase();
            setBusinessType(initialType);
            form.setFieldsValue(initialType === 'PROPIA' ? { ...initial, ...ownFormValues() } : initial);
        } else if (nextKind === 'product') {
            const rowSource = String(valueOf(initial, 'sourceType', 'SourceType') || '').toUpperCase();
            const initialSource: 'CATALOG' | 'LOCAL' = rowSource === 'LOCAL' ? 'LOCAL' : 'CATALOG';
            setProductSource(initialSource);
            setCatalogSearch('');
            const selectedCatalogProductId = String(
                valueOf(initial, 'productId', 'ProductId') || ''
            );
            const selectedCatalogProduct = [...products, ...catalogProducts].find(row => idOf(row) === selectedCatalogProductId);
            const procedure = procedureConfigurations.find(item => String(valueOf(item, 'organizationProductId', 'OrganizationProductId') || '') === String(valueOf(initial, 'organizationProductId', 'OrganizationProductId') || ''));
            const serviceId = valueOf(
                initial,
                'serviceId',
                'ServiceId'
            ) || valueOf(selectedCatalogProduct || {}, 'serviceId', 'ServiceId');
            const selectedService = services.find(item => String(valueOf(item, 'serviceId', 'ServiceId') || '') === String(serviceId || ''));
            const procedureProduct = isProcedureProduct(selectedCatalogProduct || initial) || isProcedureService(selectedService);
            setSelectedProductIsProcedure(procedureProduct);
            setProductServiceFilter(String(serviceId || ''));
            void loadCatalogProducts(String(serviceId || ''), '', 1);
            const procedureId = String(valueOf(procedure || {}, 'id', 'Id') || '');
            setProcedureAssignments([]);
            setProcedureAssignmentsLoaded(!procedureId);
            if (procedureId) {
                void loadProcedureAssignments(procedureId).then(assignments => {
                    form.setFieldsValue({
                        procedureDocumentIds: assignments.map(item => String(valueOf(item, 'documentsId', 'DocumentsId') || ''))
                    });
                });
            }
            form.setFieldsValue(row
                ? {
                    ...initial,
                    productSource: initialSource,
                    catalogProductId: valueOf(initial, 'productId', 'ProductId'),
                    organizationDescription: valueOf(initial, 'organizationDescription', 'OrganizationDescription'),
                    organizationGenericDescription: valueOf(initial, 'organizationGenericDescription', 'OrganizationGenericDescription'),
                    organizationCommercialName: valueOf(initial, 'organizationCommercialName', 'OrganizationCommercialName'),
                    procedureType: valueOf(procedure || {}, 'type', 'Type') || 'TERAPEUTICO',
                    procedureHasConsent: Boolean(valueOf(procedure || {}, 'hasConsent', 'HasConsent')),
                    procedureHasAppointment: valueOf(procedure || {}, 'hasAppointment', 'HasAppointment') || 'NO',
                    procedureAppointmentMinutesTime: Number(valueOf(procedure || {}, 'appointmentMinutesTime', 'AppointmentMinutesTime') || 0),
                    procedureHasAnesthesiologist: Boolean(valueOf(procedure || {}, 'hasAnesthesiologist', 'HasAnesthesiologist')),
                    procedureDurationMinutes: Number(valueOf(procedure || {}, 'durationMinutes', 'DurationMinutes') || 0),
                    procedureDocumentIds: []
                }
                : {
                    ...initial,
                    productSource: 'CATALOG',
                    procedureType: 'TERAPEUTICO',
                    procedureHasConsent: false,
                    procedureHasAppointment: 'NO',
                    procedureAppointmentMinutesTime: 0,
                    procedureHasAnesthesiologist: false,
                    procedureDurationMinutes: 0,
                    procedureDocumentIds: []
                });
        } else {
            setProductServiceFilter('');
            setProductSource('CATALOG');
            setCatalogSearch('');
            setSelectedProductIsProcedure(false);
            form.setFieldsValue(initial);
        }
    };
    const close = () => {
        setKind(null);
        setEditing(null);
        setBusinessType('');
        setProductServiceFilter('');
        setProductSource('CATALOG');
        setCatalogSearch('');
        setCatalogProducts([]);
        setCatalogTotal(0);
        setSelectedProductIsProcedure(false);
        form.resetFields();
    };
    const reloadCurrent = async () => {
        await loadBusinesses();
        if (selectedBusinessId) await loadAgreements(selectedBusinessId);
        if (selectedAgreementId) await loadGlobalCoverage(selectedAgreementId);
        if (selectedAgreementId) await loadGlobalProductCoverage(selectedAgreementId, globalProductPage, globalProductServiceId, globalProductSearch);
        if (selectedGroupId) await loadPlans(selectedGroupId);
        if (selectedPlanId) await loadCoverage(selectedPlanId);
    };
    const save = async (values: Row) => {
        try {
            if (!kind) return;
            if (kind === 'business') {
                const isOwn = String(values.type || '').toUpperCase() === 'PROPIA' || businessType === 'PROPIA';
                const existingSource = String(valueOf(editing || {}, 'sourceType', 'SourceType') || '').toUpperCase();
                const isLegacyLocal = !isOwn && existingSource === 'LOCAL';
                const payload = isOwn
                    ? { ...values, ...ownFormValues(), status: values.status || 'ACTIVO', observation: values.observation }
                    : isLegacyLocal
                        ? {
                            sourceType: 'LOCAL',
                            businessId: null,
                            type: values.type,
                            ruc: values.ruc,
                            name: values.name,
                            address: values.address,
                            phone: values.phone,
                            emailLegalRepresentative: values.emailLegalRepresentative,
                            status: values.status || 'ACTIVO',
                            observation: values.observation
                        }
                        : {
                            sourceType: 'CATALOG',
                            businessId: values.businessId,
                            status: values.status || 'ACTIVO',
                            observation: values.observation
                        };
                if (editing) {
                    const updatedBusinesses = await run(
                        putBusinessOrganizationAPI(organizationId, idOf(editing), payload),
                        'Empresa actualizada.'
                    );
                    setBusinesses(updatedBusinesses);
                } else {
                    const updatedBusinesses = await run(
                        postBusinessOrganizationAPI(organizationId, payload),
                        isOwn
                            ? 'Empresa propia creada. Se generó su convenio base.'
                            : 'Empresa asociada.'
                    );
                    setBusinesses(updatedBusinesses);
                }
            } else if (kind === 'agreement') {
                const call = editing ? putBusinessAgreementAPI(organizationId, selectedBusinessId, idOf(editing), values) : postBusinessAgreementAPI(organizationId, selectedBusinessId, values);
                const updatedAgreements = await run(call, editing ? 'Convenio actualizado.' : 'Convenio creado.');
                setAgreements(updatedAgreements);
            } else if (kind === 'plan') {
                const call = editing ? putCoveragePlanAPI(organizationId, selectedGroupId, idOf(editing), values) : postCoveragePlanAPI(organizationId, selectedGroupId, values);
                const updatedPlans = await run(call, editing ? 'Plan actualizado.' : 'Plan creado.');
                setPlans(updatedPlans);
            } else if (kind === 'product') {
                const isOwnBusiness = String(valueOf(selectedBusiness || {}, 'type', 'Type') || '').toUpperCase() === 'PROPIA';
                const source: 'CATALOG' | 'LOCAL' = isOwnBusiness && !selectedPlanId
                    ? (values.productSource || productSource)
                    : 'CATALOG';
                let catalogProductId = String(
                    values.catalogProductId
                    || valueOf(editing || {}, 'productId', 'ProductId')
                    || ''
                );
                let selectedProduct = [...products, ...catalogProducts].find(row => idOf(row) === catalogProductId);
                let organizationProductId = String(
                    valueOf(selectedProduct || {}, 'organizationProductId', 'OrganizationProductId')
                    || valueOf(editing || {}, 'organizationProductId', 'OrganizationProductId')
                    || ''
                );

                if (source === 'LOCAL') {
                    const serviceId = String(productServiceFilter || values.serviceId || '');
                    if (!serviceId) throw new Error('Seleccione el servicio de str.services para el producto propio.');
                    if (!String(values.organizationDescription || '').trim()) {
                        throw new Error('Ingrese la descripción del producto propio.');
                    }
                    const localAlreadyExists = Boolean(editing && organizationProductId);
                    if (!localAlreadyExists) {
                        const localResponse = await postOrganizationLocalProductAPI(organizationId, {
                            serviceId,
                            description: String(values.organizationDescription).trim(),
                            genericDescription: String(values.organizationGenericDescription || values.organizationDescription).trim(),
                            commercialName: String(values.organizationCommercialName || values.organizationDescription).trim()
                        });
                        const localFailure = responseError(localResponse);
                        if (localFailure) throw new Error(String(localFailure));
                        const updatedOrganizationProducts = rowsOf(localResponse);
                        setProducts(updatedOrganizationProducts);
                        const local = updatedOrganizationProducts.find(item =>
                            String(valueOf(item, 'sourceType', 'SourceType') || '').toUpperCase() === 'LOCAL'
                            && String(valueOf(item, 'serviceId', 'ServiceId') || '') === serviceId
                            && String(valueOf(item, 'organizationDescription', 'OrganizationDescription') || '') === String(values.organizationDescription).trim()
                        ) || updatedOrganizationProducts[updatedOrganizationProducts.length - 1];
                        selectedProduct = local;
                        catalogProductId = String(valueOf(local || {}, 'id', 'Id') || '');
                        organizationProductId = String(valueOf(local || {}, 'organizationProductId', 'OrganizationProductId') || '');
                    } else if (selectedProduct) {
                        // La edición de un producto propio conserva el código
                        // oficial y solo actualiza sus nombres/descripciones.
                        const associationResponse = await putOrganizationManagementProductAPI(organizationId, organizationProductId, {
                            productId: catalogProductId,
                            organizationDescription: values.organizationDescription || null,
                            organizationGenericDescription: values.organizationGenericDescription || null,
                            organizationCommercialName: values.organizationCommercialName || null,
                            status: 'ACTIVO'
                        });
                        const associationFailure = responseError(associationResponse);
                        if (associationFailure) throw new Error(String(associationFailure));
                        const updatedOrganizationProducts = rowsOf(associationResponse);
                        setProducts(updatedOrganizationProducts);
                        selectedProduct = updatedOrganizationProducts.find(item =>
                            String(valueOf(item, 'organizationProductId', 'OrganizationProductId') || '') === organizationProductId
                        ) || selectedProduct;
                    }
                } else {
                    if (!selectedProduct) {
                        throw new Error('Seleccione un producto activo del catálogo global.');
                    }
                }

                if (isOwnBusiness && source === 'CATALOG') {
                    const associationPayload = {
                        productId: catalogProductId,
                        organizationDescription: values.organizationDescription || null,
                        organizationGenericDescription: values.organizationGenericDescription || null,
                        organizationCommercialName: values.organizationCommercialName || null,
                        status: 'ACTIVO'
                    };
                    const associationResponse = organizationProductId
                        ? await putOrganizationManagementProductAPI(organizationId, organizationProductId, associationPayload)
                        : await postOrganizationManagementProductAPI(organizationId, associationPayload);
                    const associationFailure = responseError(associationResponse);
                    if (associationFailure) throw new Error(String(associationFailure));
                    // POST/PUT devuelve la colección completa, igual que el
                    // GET de productos. Reemplazamos el estado local y
                    // localizamos la asociación recién guardada dentro de
                    // esa colección, sin repetir una consulta.
                    const updatedOrganizationProducts = rowsOf(associationResponse);
                    setProducts(updatedOrganizationProducts);
                    const association = updatedOrganizationProducts.find(item =>
                        String(valueOf(item, 'id', 'Id') || '') === catalogProductId
                        || String(valueOf(item, 'organizationProductId', 'OrganizationProductId') || '') === organizationProductId
                    ) || {};
                    organizationProductId = String(
                        valueOf(association, 'organizationProductId', 'OrganizationProductId')
                        || organizationProductId
                        || ''
                    );
                }

                if (!organizationProductId) {
                    throw new Error('El producto debe estar asociado previamente a la organización.');
                }

                // pdt.procedure es configuración de la organización propia;
                // un convenio de aseguradora/tercero solo reutiliza ese
                // producto y no puede modificar sus parámetros clínicos.
                if (selectedProductIsProcedure && isOwnBusiness && !selectedPlanId) {
                    const procedureId = String(
                        valueOf(procedureConfigurations.find(item => String(valueOf(item, 'organizationProductId', 'OrganizationProductId') || '') === organizationProductId) || {}, 'id', 'Id') || ''
                    );
                    const selectedDocumentIds = Array.isArray(values.procedureDocumentIds)
                        ? values.procedureDocumentIds.map((value: any) => String(value)).filter(Boolean)
                        : [];
                    const selectedDocuments = selectedDocumentIds
                        .map(documentId => procedureDocuments.find(document => idOf(document) === documentId))
                        .filter((document): document is Row => Boolean(document));
                    if (Boolean(values.procedureHasConsent)
                        && !selectedDocuments.some(document => isConsentDocument(document))) {
                        throw new Error(
                            'Este procedimiento requiere consentimiento. Seleccione un documento activo de tipo Consentimiento informado para poder continuar.'
                        );
                    }
                    const procedurePayload = {
                        organizationProductId,
                        type: values.procedureType || 'TERAPEUTICO',
                        hasConsent: Boolean(values.procedureHasConsent),
                        hasAppointment: values.procedureHasAppointment || 'NO',
                        appointmentMinutesTime: Number(values.procedureAppointmentMinutesTime || 0),
                        hasAnesthesiologist: Boolean(values.procedureHasAnesthesiologist),
                        durationMinutes: Number(values.procedureDurationMinutes || 0)
                    };
                    const procedureResponse = procedureId
                        ? await putProcedureConfigurationAPI(organizationId, procedureId, procedurePayload)
                        : await postProcedureConfigurationAPI(organizationId, procedurePayload);
                    const procedureFailure = responseError(procedureResponse);
                    if (procedureFailure) throw new Error(String(procedureFailure));
                    const updatedProcedureConfigurations = rowsOf(procedureResponse);
                    setProcedureConfigurations(updatedProcedureConfigurations);
                    const savedProcedure = updatedProcedureConfigurations.find(item => String(valueOf(item, 'organizationProductId', 'OrganizationProductId') || '') === organizationProductId);
                    const savedProcedureId = String(valueOf(savedProcedure || {}, 'id', 'Id') || procedureId || '');
                    if (!savedProcedureId) throw new Error('No se pudo identificar el procedimiento guardado.');

                    // La selección del formulario se sincroniza con
                    // pdt.document_assignment. El consentimiento informado
                    // se asigna con uso FIRMAR; el resto de documentos se
                    // completa durante el resultado del procedimiento.
                    let currentAssignments = procedureAssignments;
                    if (!procedureAssignmentsLoaded && procedureId) {
                        currentAssignments = await loadProcedureAssignments(savedProcedureId);
                    }
                    const currentByDocument = new Map(
                        currentAssignments.map(item => [String(valueOf(item, 'documentsId', 'DocumentsId') || ''), item])
                    );
                    let updatedAssignments = currentAssignments;
                    for (const documentId of selectedDocumentIds.filter(id => id && !currentByDocument.has(id))) {
                        const document = procedureDocuments.find(item => idOf(item) === documentId);
                        const assignmentResponse = await postProcedureDocumentAssignmentAPI(
                            organizationId,
                            savedProcedureId,
                            {
                                documentsId: documentId,
                                usage: isConsentDocument(document) ? 'FIRMAR' : 'COMPLETAR_RESULTADO'
                            }
                        );
                        const assignmentFailure = responseError(assignmentResponse);
                        if (assignmentFailure) throw new Error(String(assignmentFailure));
                        updatedAssignments = rowsOf(assignmentResponse);
                    }
                    for (const assignment of currentAssignments) {
                        const documentId = String(valueOf(assignment, 'documentsId', 'DocumentsId') || '');
                        if (!documentId || !selectedDocumentIds.includes(documentId)) continue;
                        const document = procedureDocuments.find(item => idOf(item) === documentId);
                        const expectedUsage = isConsentDocument(document)
                            || isConsentDocument(assignment)
                            ? 'FIRMAR'
                            : 'COMPLETAR_RESULTADO';
                        const currentUsage = String(valueOf(assignment, 'usage', 'Usage') || '').toUpperCase();
                        if (currentUsage !== expectedUsage && idOf(assignment)) {
                            const assignmentResponse = await putProcedureDocumentAssignmentAPI(
                                organizationId,
                                savedProcedureId,
                                idOf(assignment),
                                { documentsId: documentId, usage: expectedUsage }
                            );
                            const assignmentFailure = responseError(assignmentResponse);
                            if (assignmentFailure) throw new Error(String(assignmentFailure));
                            updatedAssignments = rowsOf(assignmentResponse);
                        }
                    }
                    for (const assignment of currentAssignments) {
                        const documentId = String(valueOf(assignment, 'documentsId', 'DocumentsId') || '');
                        if (documentId && !selectedDocumentIds.includes(documentId)) {
                            const assignmentResponse = await deleteProcedureDocumentAssignmentAPI(
                                organizationId,
                                savedProcedureId,
                                idOf(assignment)
                            );
                            const assignmentFailure = responseError(assignmentResponse);
                            if (assignmentFailure) throw new Error(String(assignmentFailure));
                            updatedAssignments = rowsOf(assignmentResponse);
                        }
                    }
                    setProcedureAssignments(updatedAssignments);
                }

                const { catalogProductId: _catalogProductId, productSource: _productSource, organizationCode: _organizationCode,
                    organizationDescription: _organizationDescription,
                    organizationGenericDescription: _organizationGenericDescription,
                    organizationCommercialName: _organizationCommercialName,
                    procedureType: _procedureType,
                    procedureHasConsent: _procedureHasConsent,
                    procedureHasAppointment: _procedureHasAppointment,
                    procedureAppointmentMinutesTime: _procedureAppointmentMinutesTime,
                    procedureHasAnesthesiologist: _procedureHasAnesthesiologist,
                    procedureDurationMinutes: _procedureDurationMinutes,
                    procedureDocumentIds: _procedureDocumentIds, ...coverageValues } = values;
                const coveragePayload = { ...coverageValues, organizationProductId };
                const editingPlanCoverage = Boolean(editing && editing.isConfigured !== false && idOf(editing));
                const call = selectedPlanId
                    ? (editingPlanCoverage
                        ? putProductsCoverageAPI(organizationId, selectedPlanId, idOf(editing || {}), coveragePayload)
                        : postProductsCoverageAPI(organizationId, selectedPlanId, coveragePayload))
                    : (editing && idOf(editing)
                        ? putGlobalProductsCoverageAPI(organizationId, selectedAgreementId, idOf(editing || {}), coveragePayload)
                        : postGlobalProductsCoverageAPI(organizationId, selectedAgreementId, coveragePayload));
                const updatedProducts = await run(
                    call,
                    editingPlanCoverage || (!selectedPlanId && editing && idOf(editing))
                        ? 'Precio/cobertura de producto actualizada.'
                        : (selectedPlanId ? 'Cobertura de producto configurada en el plan.' : 'Producto agregado al convenio.')
                );
                if (selectedPlanId) {
                    setProductCoverage(updatedProducts);
                } else {
                    // POST/PUT devuelve la colección vigente. Aplicamos el mismo
                    // filtro/paginación de la tabla sin hacer un GET adicional.
                    const normalizedSearch = globalProductSearch.trim().toLowerCase();
                    const filteredProducts = updatedProducts.filter(row => {
                        const rowServiceId = String(valueOf(row, 'serviceId', 'ServiceId') || '');
                        const matchesService = !globalProductServiceId || rowServiceId === globalProductServiceId;
                        const text = [
                            valueOf(row, 'productCode', 'ProductCode', 'code', 'Code'),
                            valueOf(row, 'productName', 'ProductName', 'description', 'Description'),
                            valueOf(row, 'commercialName', 'CommercialName', 'organizationDescription', 'OrganizationDescription')
                        ].map(value => String(value || '').toLowerCase()).join(' ');
                        return matchesService && (!normalizedSearch || text.includes(normalizedSearch));
                    });
                    setGlobalProductTotal(filteredProducts.length);
                    const maxPage = Math.max(1, Math.ceil(filteredProducts.length / globalProductPageSize));
                    const nextPage = Math.min(globalProductPage, maxPage);
                    if (nextPage !== globalProductPage) setGlobalProductPage(nextPage);
                    setGlobalProductCoverage(filteredProducts.slice((nextPage - 1) * globalProductPageSize, nextPage * globalProductPageSize));
                }
            } else {
                const editingPlanCoverage = Boolean(editing && editing.isConfigured !== false && idOf(editing));
                const call = selectedPlanId
                    ? (editingPlanCoverage ? putServiceCoverageAPI(organizationId, selectedPlanId, idOf(editing || {}), values) : postServiceCoverageAPI(organizationId, selectedPlanId, values))
                    : (editing && idOf(editing) ? putGlobalServiceCoverageAPI(organizationId, selectedAgreementId, idOf(editing || {}), values) : postGlobalServiceCoverageAPI(organizationId, selectedAgreementId, values));
                const updatedServices = await run(
                    call,
                    editingPlanCoverage || (!selectedPlanId && editing && idOf(editing))
                        ? 'Precio/cobertura de servicio actualizada.'
                        : (selectedPlanId ? 'Cobertura de servicio configurada en el plan.' : 'Servicio agregado al convenio.')
                );
                selectedPlanId ? setServiceCoverage(updatedServices) : setGlobalServiceCoverage(updatedServices);
            }
            // Las mutaciones ya retornaron la colección vigente. El GET queda
            // reservado para la carga inicial y el botón Actualizar.
            close();
        } catch (e: any) { setError(e?.message || 'No se pudo guardar.'); }
    };
    const inactivate = async (row: Row) => {
        if (String(valueOf(row, 'type', 'Type') || '').toUpperCase() === 'PROPIA') {
            message.warning('La empresa PROPIA es obligatoria y no se puede inactivar.');
            return;
        }
        try {
            const updatedBusinesses = await run(
                inactivateBusinessOrganizationAPI(organizationId, idOf(row)),
                'Empresa inactivada.'
            );
            setBusinesses(updatedBusinesses);
        }
        catch (e: any) { setError(e?.message || 'No se pudo inactivar la empresa.'); }
    };

    const parseJsonValue = (value: any): any => {
        if (typeof value === 'string') {
            try { return JSON.parse(value); } catch { return value; }
        }
        // El Gateway puede envolver el cuerpo 4xx del microservicio dentro de
        // `message`. Recuperamos ese JSON para mostrar las filas y motivos.
        if (value && typeof value === 'object') {
            const nestedMessage = value.message ?? value.Message;
            if (typeof nestedMessage === 'string') {
                const parsedMessage = parseJsonValue(nestedMessage);
                if (parsedMessage !== nestedMessage) return parsedMessage;
            }
        }
        return value;
    };
    const parseNumber = (value: any, fallback = -1) => {
        if (value === undefined || value === null || String(value).trim() === '') return fallback;
        const parsed = Number(String(value).trim().replace(',', '.'));
        return Number.isFinite(parsed) ? parsed : fallback;
    };
    const normalizeHeader = (value: any) => String(value || '')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase().replace(/[\s_-]+/g, '');
    const valueFromImportRow = (row: Row, names: string[]) => {
        const entries = Object.keys(row).map(key => [normalizeHeader(key), row[key]] as [string, any]);
        const aliases = names.map(normalizeHeader);
        const match = entries.find(([key]) => aliases.includes(key));
        return match?.[1];
    };
    const normalizeImportRow = (row: Row): Row => ({
        productId: String(valueFromImportRow(row, ['productId', 'idProducto', 'productoId']) || '').trim() || undefined,
        productCode: String(valueFromImportRow(row, ['productCode', 'codigoProducto', 'codigo', 'code']) || '').trim() || undefined,
        organizationCode: String(valueFromImportRow(row, ['organizationCode', 'codigoOrganizacion', 'codigoLocal']) || '').trim() || undefined,
        organizationDescription: String(valueFromImportRow(row, ['organizationDescription', 'descripcionOrganizacion', 'descripcionLocal']) || '').trim() || undefined,
        organizationGenericDescription: String(valueFromImportRow(row, ['organizationGenericDescription', 'descripcionGenericaOrganizacion']) || '').trim() || undefined,
        organizationCommercialName: String(valueFromImportRow(row, ['organizationCommercialName', 'nombreComercialOrganizacion', 'nombreLocal']) || '').trim() || undefined,
        status: String(valueFromImportRow(row, ['status', 'estado']) || 'ACTIVO').trim().toUpperCase(),
        price: parseNumber(valueFromImportRow(row, ['price', 'precio']), -1),
        coverageValue: parseNumber(valueFromImportRow(row, ['coverageValue', 'valorCobertura', 'cobertura']), 0),
        coverageType: String(valueFromImportRow(row, ['coverageType', 'tipoCobertura']) || 'PORCENTAJE').trim().toUpperCase(),
        copaymentValue: parseNumber(valueFromImportRow(row, ['copaymentValue', 'valorCopago', 'copago']), 100),
        copaymentType: String(valueFromImportRow(row, ['copaymentType', 'tipoCopago']) || 'PORCENTAJE').trim().toUpperCase(),
        description: String(valueFromImportRow(row, ['description', 'observacion', 'descripcion']) || '').trim() || undefined
    });
    const openBulk = () => {
        setBulkRows([]); setBulkFileName(''); setBulkResult(null); setBulkOpen(true);
    };
    const downloadBulkTemplate = () => {
        const headers = [
            'codigo_producto', 'product_id', 'nombre_local',
            'descripcion_local', 'descripcion_generica_organizacion', 'precio'
        ];
        const example = ['CODIGO_DEL_CATALOGO', '', '', '', '', 0];
        if (!selectedBusinessIsOwn) {
            headers.push('tipo_cobertura', 'valor_cobertura', 'tipo_copago', 'valor_copago');
            example.push('PORCENTAJE', 0, 'PORCENTAJE', 100);
        }
        headers.push('estado', 'observacion');
        example.push('ACTIVO', '');
        const sheet = XLSX.utils.aoa_to_sheet([headers, example]);
        sheet['!cols'] = headers.map((header) => ({ wch: Math.max(16, header.length + 2) }));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, sheet, 'Lista de precios');
        XLSX.writeFile(workbook, 'plantilla_lista_precios.xlsx');
    };
    const handleBulkFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const sourceRows = XLSX.utils.sheet_to_json<Row>(firstSheet, { defval: '' });
            const normalized = sourceRows.map(normalizeImportRow).filter(row => row.productId || row.productCode || row.price >= 0);
            if (!normalized.length) throw new Error('El archivo no contiene filas de productos.');
            if (normalized.length > 5000) throw new Error('El archivo supera el máximo de 5000 filas.');
            setBulkRows(normalized);
            setBulkFileName(file.name);
            setBulkResult(null);
            message.success(`${normalized.length} filas cargadas para validar.`);
        } catch (e: any) {
            setBulkRows([]); setBulkFileName('');
            message.error(e?.message || 'No se pudo leer el archivo Excel.');
        }
    };
    const submitBulk = async () => {
        if (!selectedBusinessId || !selectedPlanId) return;
        if (!bulkRows.length) { message.warning('Seleccione un archivo con al menos una fila.'); return; }
        setBulkSubmitting(true);
        try {
            const response = await importProductPriceListAPI(
                organizationId,
                selectedBusinessId,
                selectedPlanId,
                { rows: bulkRows }
            );
            const failure = responseError(response);
            const payload = parseJsonValue(failure ? response.Error : response.Response);
            if (failure) {
                setBulkResult(payload && typeof payload === 'object' ? payload : { success: false, rows: [] });
                message.error('La carga fue rechazada. No se guardó ninguna fila.');
                return;
            }
            setBulkResult(payload && typeof payload === 'object' ? payload : { success: true });
            message.success(payload?.applied === false ? 'Validación completada sin guardar cambios.' : 'Lista de precios procesada correctamente.');
            // La carga masiva conserva el detalle de filas y, cuando aplica,
            // devuelve `items` con la colección completa del plan.
            if (Array.isArray(payload?.items)) {
                setProductCoverage(payload.items);
            }
        } catch (e: any) {
            message.error(e?.message || 'No se pudo procesar la carga masiva.');
        } finally { setBulkSubmitting(false); }
    };

    const manageBusiness = (row: Row) => {
        setSelectedBusinessId(idOf(row));
        setSelectedAgreementId('');
        setSelectedGroupId('');
        setSelectedPlanId('');
        setNavigationLevel('agreements');
    };
    const manageAgreement = (row: Row) => {
        setSelectedAgreementId(idOf(row));
        setSelectedGroupId(idOf(row));
        setSelectedPlanId('');
        setNavigationLevel('plans');
    };
    const managePlan = (row: Row) => {
        setSelectedPlanId(idOf(row));
        setCoverageTab('products');
        setNavigationLevel('coverage');
    };
    const goBusinesses = () => {
        setNavigationLevel('businesses');
        setSelectedBusinessId('');
        setSelectedAgreementId('');
        setSelectedGroupId('');
        setSelectedPlanId('');
    };
    const goAgreements = () => {
        setNavigationLevel('agreements');
        setSelectedAgreementId('');
        setSelectedGroupId('');
        setSelectedPlanId('');
    };
    const goPlans = () => {
        setNavigationLevel('plans');
        setSelectedPlanId('');
    };
    const goMatrix = () => {
        setNavigationLevel('matrix');
    };

    const selectedBusiness = useMemo(
        () => businesses.find(row => idOf(row) === selectedBusinessId),
        [businesses, selectedBusinessId]
    );
    const selectedBusinessIsOwn = String(valueOf(selectedBusiness || {}, 'type', 'Type') || '').toUpperCase() === 'PROPIA';
    const selectedAgreement = useMemo(
        () => agreements.find(row => idOf(row) === selectedAgreementId),
        [agreements, selectedAgreementId]
    );
    const selectedPlan = useMemo(
        () => plans.find(row => idOf(row) === selectedPlanId),
        [plans, selectedPlanId]
    );
    // Los elementos del plan se proyectan desde la cobertura global del
    // convenio. Si aún no existe una fila en el plan, queda visible como
    // pendiente de configurar; nunca se crea un producto independiente.
    const planProductRows = useMemo<Row[]>(() => {
        const configured = new Map(
            productCoverage.map(row => [
                String(valueOf(row, 'organizationProductId', 'OrganizationProductId') || ''),
                row
            ])
        );
        const globalIds = new Set<string>();
        const rows: Row[] = globalProductCoverage.map(global => {
            const organizationProductId = String(valueOf(global, 'organizationProductId', 'OrganizationProductId') || '');
            globalIds.add(organizationProductId);
            const existing = configured.get(organizationProductId);
            if (existing) return { ...existing, isConfigured: true, globalPrice: valueOf(global, 'price', 'Price') };
            return {
                ...global,
                id: `plan-product-${organizationProductId}`,
                coveragePlanId: selectedPlanId,
                price: valueOf(global, 'price', 'Price') || 0,
                isConfigured: false
            };
        });
        return rows.concat(productCoverage
            .filter(row => !globalIds.has(String(valueOf(row, 'organizationProductId', 'OrganizationProductId') || '')))
            .map(row => ({ ...row, isConfigured: true })));
    }, [globalProductCoverage, productCoverage, selectedPlanId]);
    const planServiceRows = useMemo<Row[]>(() => {
        const configured = new Map(
            serviceCoverage.map(row => [
                String(valueOf(row, 'serviceOrganizationId', 'ServiceOrganizationId') || ''),
                row
            ])
        );
        const globalIds = new Set<string>();
        const rows: Row[] = globalServiceCoverage.map(global => {
            const serviceOrganizationId = String(valueOf(global, 'serviceOrganizationId', 'ServiceOrganizationId') || '');
            globalIds.add(serviceOrganizationId);
            const existing = configured.get(serviceOrganizationId);
            if (existing) return { ...existing, isConfigured: true, globalPrice: valueOf(global, 'price', 'Price') };
            return {
                ...global,
                id: `plan-service-${serviceOrganizationId}`,
                coveragePlanId: selectedPlanId,
                price: valueOf(global, 'price', 'Price') || 0,
                isConfigured: false
            };
        });
        return rows.concat(serviceCoverage
            .filter(row => !globalIds.has(String(valueOf(row, 'serviceOrganizationId', 'ServiceOrganizationId') || '')))
            .map(row => ({ ...row, isConfigured: true })));
    }, [globalServiceCoverage, serviceCoverage, selectedPlanId]);

    const commonFields = (statusOptions: string[] = ['ACTIVO', 'INACTIVO']) => <Form.Item name="status" label="Estado" rules={[{ required: true }]}><Select options={statusOptions.map(value => ({ value }))} /></Form.Item>;
    const coverageFields = (service: boolean) => {
        const isOwnBusiness = String(valueOf(selectedBusiness || {}, 'type', 'Type') || '').toUpperCase() === 'PROPIA';
        const agreementProductIds = new Set(globalProductCoverage.map(row => String(valueOf(row, 'organizationProductId', 'OrganizationProductId') || '')));
        const agreementServiceIds = new Set(globalServiceCoverage.map(row => String(valueOf(row, 'serviceOrganizationId', 'ServiceOrganizationId') || '')));
        const productServiceOptions = services
            .map(row => {
                const serviceId = String(valueOf(row, 'serviceId', 'ServiceId') || '');
                const serviceOrganizationId = idOf(row);
                return {
                    value: serviceId,
                    label: labelOf(row, 'name', 'Name', 'serviceName', 'ServiceName') || serviceId,
                    serviceOrganizationId
                };
            })
            .filter(option => Boolean(option.value));
        const registeredProductOptions = products
            .filter(row => (isOwnBusiness || valueOf(row, 'organizationProductId', 'OrganizationProductId'))
                && (!selectedPlanId || agreementProductIds.has(String(valueOf(row, 'organizationProductId', 'OrganizationProductId') || idOf(row)))))
            .filter(row => !productServiceFilter
                || String(valueOf(row, 'serviceId', 'ServiceId') || '') === productServiceFilter)
            .map(row => {
                const code = String(valueOf(row, 'code', 'Code') || '');
                const name = String(valueOf(row, 'organizationCommercialName', 'OrganizationCommercialName', 'commercialName', 'CommercialName') || '');
                return {
                    value: idOf(row),
                    label: `${code ? `${code} · ` : ''}${name || labelOf(row, 'commercialName', 'CommercialName') || idOf(row)}`
                };
            });
        const globalCatalogOptions = catalogProducts
            .filter(row => !productServiceFilter
                || String(valueOf(row, 'serviceId', 'ServiceId') || '') === productServiceFilter)
            .map(row => {
                const code = String(valueOf(row, 'code', 'Code') || '');
                const name = String(valueOf(row, 'name', 'Name', 'commercialName', 'CommercialName') || '');
                return {
                    value: idOf(row),
                    label: `${code ? `${code} · ` : ''}${name || idOf(row)}`
                };
            });
        const productOptions = isOwnBusiness && !selectedPlanId && productSource === 'CATALOG'
            ? globalCatalogOptions
            : registeredProductOptions;
        const options = service
            ? services
                .filter(row => !selectedPlanId || agreementServiceIds.has(idOf(row)))
                .map(row => ({ value: idOf(row), label: labelOf(row, 'name', 'serviceName') || idOf(row) }))
            : productOptions;

        return <>
            {selectedPlanId && ((service && !agreementServiceIds.size) || (!service && !agreementProductIds.size)) && <Alert
                type="warning"
                showIcon
                message="Primero registre el elemento en la cobertura global"
                description="Los planes solo pueden configurar copagos y coberturas de productos o servicios que ya estén registrados en el convenio."
                style={{ marginBottom: 16 }}
            />}
            {!service && <Form.Item label="Filtrar por servicio (str.services)">
                <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Todos los servicios"
                    value={productServiceFilter || undefined}
                    options={productServiceOptions.map(option => ({ value: option.value, label: option.label }))}
                    onChange={(value) => {
                        const nextServiceId = String(value || '');
                        setProductServiceFilter(nextServiceId);
                        setCatalogSearch('');
                        if (isOwnBusiness && !selectedPlanId && productSource === 'CATALOG') {
                            void loadCatalogProducts(nextServiceId, '', 1);
                        }
                        const selectedService = services.find(item => String(valueOf(item, 'serviceId', 'ServiceId') || '') === nextServiceId);
                        if (nextServiceId) setSelectedProductIsProcedure(isProcedureService(selectedService));
                        // Evita conservar un producto que ya no pertenece al filtro.
                        const selectedProductId = form.getFieldValue('catalogProductId');
                        if (selectedProductId && nextServiceId) {
                            const selected = products.find(row => idOf(row) === String(selectedProductId));
                            if (String(valueOf(selected || {}, 'serviceId', 'ServiceId') || '') !== nextServiceId) {
                                form.setFieldsValue({
                                    catalogProductId: undefined,
                                    organizationDescription: undefined,
                                    organizationGenericDescription: undefined,
                                    organizationCommercialName: undefined
                                });
                            }
                        }
                    }}
                />
            </Form.Item>}
            {!service && isOwnBusiness && !selectedPlanId && <Form.Item name="productSource" label="Origen del producto" rules={[{ required: true, message: 'Seleccione el origen del producto' }]}>
                <Select
                    options={[
                        { value: 'CATALOG', label: 'Usar producto del catálogo global' },
                        { value: 'LOCAL', label: 'Crear producto propio' }
                    ]}
                    onChange={(value) => {
                        const nextSource = value as 'CATALOG' | 'LOCAL';
                        setProductSource(nextSource);
                        form.setFieldsValue({ catalogProductId: undefined });
                        setSelectedProductIsProcedure(false);
                        if (nextSource === 'CATALOG' && productServiceFilter) {
                            void loadCatalogProducts(productServiceFilter, catalogSearch, 1);
                        }
                    }}
                />
            </Form.Item>}
            {(service || !isOwnBusiness || Boolean(selectedPlanId) || productSource === 'CATALOG') && <>
            <Form.Item name={service ? 'serviceOrganizationId' : 'catalogProductId'} label={service ? 'Servicio de la organización' : 'Producto del catálogo global'} rules={[{ required: true, message: 'Seleccione un elemento' }]}>
                <Select
                    showSearch
                    optionFilterProp="label"
                    filterOption={service || !isOwnBusiness || Boolean(selectedPlanId)}
                    loading={!service && isOwnBusiness && !selectedPlanId && catalogLoading}
                    options={options}
                    onSearch={(text) => {
                        if (!service && isOwnBusiness && !selectedPlanId && productSource === 'CATALOG' && productServiceFilter) {
                            setCatalogSearch(text);
                            void loadCatalogProducts(productServiceFilter, text, 1);
                        }
                    }}
                    onChange={(value) => {
                        if (service) return;
                        const product = [...products, ...catalogProducts].find(row => idOf(row) === String(value));
                        const selectedService = services.find(item => String(valueOf(item, 'serviceId', 'ServiceId') || '') === productServiceFilter);
                        const procedureProduct = isProcedureProduct(product) || isProcedureService(selectedService);
                        setSelectedProductIsProcedure(procedureProduct);
                        if (product) {
                            const procedure = procedureConfigurations.find(item => String(valueOf(item, 'organizationProductId', 'OrganizationProductId') || '') === String(valueOf(product, 'organizationProductId', 'OrganizationProductId') || ''));
                            form.setFieldsValue({
                                organizationDescription: valueOf(product, 'organizationDescription', 'OrganizationDescription') || undefined,
                                organizationGenericDescription: valueOf(product, 'organizationGenericDescription', 'OrganizationGenericDescription') || undefined,
                                organizationCommercialName: valueOf(product, 'organizationCommercialName', 'OrganizationCommercialName') || undefined,
                                procedureType: valueOf(procedure || {}, 'type', 'Type') || 'TERAPEUTICO',
                                procedureHasConsent: Boolean(valueOf(procedure || {}, 'hasConsent', 'HasConsent')),
                                procedureHasAppointment: valueOf(procedure || {}, 'hasAppointment', 'HasAppointment') || 'NO',
                                procedureAppointmentMinutesTime: Number(valueOf(procedure || {}, 'appointmentMinutesTime', 'AppointmentMinutesTime') || 0),
                                procedureHasAnesthesiologist: Boolean(valueOf(procedure || {}, 'hasAnesthesiologist', 'HasAnesthesiologist')),
                                procedureDurationMinutes: Number(valueOf(procedure || {}, 'durationMinutes', 'DurationMinutes') || 0),
                                procedureDocumentIds: []
                            });
                            const procedureId = String(valueOf(procedure || {}, 'id', 'Id') || '');
                            setProcedureAssignments([]);
                            setProcedureAssignmentsLoaded(!procedureId);
                            if (procedureId) {
                                void loadProcedureAssignments(procedureId).then(assignments => {
                                    form.setFieldsValue({
                                        procedureDocumentIds: assignments.map(item => String(valueOf(item, 'documentsId', 'DocumentsId') || ''))
                                    });
                                });
                            }
                        }
                    }}
                />
            </Form.Item>
            {!service && isOwnBusiness && !selectedPlanId && productSource === 'CATALOG' && productServiceFilter && <Pagination
                size="small"
                current={catalogPage}
                pageSize={catalogPageSize}
                total={catalogTotal}
                showSizeChanger={false}
                onChange={(page) => void loadCatalogProducts(productServiceFilter, catalogSearch, page)}
                style={{ marginBottom: 16 }}
            />}
            </>}
            {!service && isOwnBusiness && !selectedPlanId && productSource === 'LOCAL' && <>
                <Alert
                    type="info"
                    showIcon
                    message="Producto propio"
                    description="El producto se creará en pdt.products con un serviceId de str.services. El código oficial se genera en el backend y no se modifica desde la organización."
                    style={{ marginBottom: 16 }}
                />
            </>}
            {!service && isOwnBusiness && productSource === 'CATALOG' && <>
                <Alert
                    type="info"
                    showIcon
                    message="Código oficial del catálogo"
                    description="El código, servicio y categoría pertenecen al catálogo de Blue y no se pueden cambiar. Solo puede personalizar los nombres y descripciones de este producto para su organización."
                    style={{ marginBottom: 16 }}
                />
                <Form.Item name="organizationDescription" label="Descripción propia"><Input placeholder="Opcional; se usa la descripción del catálogo si queda vacío" /></Form.Item>
                <Form.Item name="organizationGenericDescription" label="Descripción genérica propia"><Input placeholder="Opcional" /></Form.Item>
                <Form.Item name="organizationCommercialName" label="Nombre comercial propio"><Input placeholder="Opcional; se usa el nombre del catálogo si queda vacío" /></Form.Item>
            </>}
            {!service && isOwnBusiness && !selectedPlanId && productSource === 'LOCAL' && <>
                <Form.Item name="organizationDescription" label="Nombre / descripción del producto" rules={[{ required: true, message: 'Ingrese la descripción del producto propio' }]}><Input /></Form.Item>
                <Form.Item name="organizationGenericDescription" label="Descripción genérica"><Input placeholder="Opcional; se usa la descripción principal si queda vacío" /></Form.Item>
                <Form.Item name="organizationCommercialName" label="Nombre comercial"><Input placeholder="Opcional; se usa la descripción principal si queda vacío" /></Form.Item>
            </>}
            {!service && isOwnBusiness && selectedProductIsProcedure && !selectedPlanId && <>
                <Alert
                    type="info"
                    showIcon
                    message="Configuración del procedimiento"
                    description="Este producto pertenece al servicio PROCEDIMIENTOS. Su configuración se guarda junto con el producto de la organización; no necesita abrir otro módulo."
                    style={{ marginBottom: 16 }}
                />
                <AntRow gutter={12}>
                    <Col span={12}><Form.Item name="procedureType" label="Tipo de procedimiento" rules={[{ required: true }]}><Select options={[{ value: 'DIAGNOSTICO', label: 'Diagnóstico' }, { value: 'TERAPEUTICO', label: 'Terapéutico' }]} /></Form.Item></Col>
                    <Col span={12}><Form.Item name="procedureHasAppointment" label="¿Requiere cita?" rules={[{ required: true }]}><Select options={[{ value: 'SI', label: 'Sí' }, { value: 'NO', label: 'No' }, { value: 'OPCIONAL', label: 'Opcional' }]} /></Form.Item></Col>
                </AntRow>
                <AntRow gutter={12}>
                    <Col span={12}><Form.Item name="procedureAppointmentMinutesTime" label="Duración de cita (minutos)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                    <Col span={12}><Form.Item name="procedureDurationMinutes" label="Duración del procedimiento (minutos)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
                </AntRow>
                <AntRow gutter={12}>
                    <Col span={12}><Form.Item name="procedureHasConsent" label="Consentimiento"><Select options={[{ value: true, label: 'Requiere consentimiento' }, { value: false, label: 'No requiere consentimiento' }]} /></Form.Item></Col>
                    <Col span={12}><Form.Item name="procedureHasAnesthesiologist" label="Anestesiólogo"><Select options={[{ value: true, label: 'Requiere anestesiólogo' }, { value: false, label: 'No requiere anestesiólogo' }]} /></Form.Item></Col>
                </AntRow>
                <Form.Item
                    name="procedureDocumentIds"
                    label="Documentos del procedimiento"
                    extra="Seleccione los documentos operativos activos que se utilizarán en este procedimiento."
                >
                    <Select
                        mode="multiple"
                        showSearch
                        optionFilterProp="label"
                        placeholder={procedureDocuments.length ? 'Seleccione documentos' : 'No hay documentos activos configurados'}
                        options={procedureDocuments.map(document => ({
                            value: idOf(document),
                            label: `${valueOf(document, 'name', 'Name') || 'Documento'} (${String(valueOf(document, 'format', 'Format') || '').toUpperCase()})`
                        }))}
                    />
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(previous, current) => previous.procedureHasConsent !== current.procedureHasConsent}>
                    {({ getFieldValue }) => Boolean(getFieldValue('procedureHasConsent'))
                        && !procedureDocuments.some(document => isConsentDocument(document))
                        ? <Alert
                            type="warning"
                            showIcon
                            message="Falta configurar el consentimiento informado"
                            description="Configure primero un documento activo con código CONSENTIMIENTO_INFORMADO para poder guardar este procedimiento."
                            style={{ marginBottom: 16 }}
                        />
                        : null}
                </Form.Item>
            </>}
            {!service && !isOwnBusiness && <Alert
                type="warning"
                showIcon
                message="Producto de la organización"
                description="Para una aseguradora o tercero solo se pueden seleccionar productos que la organización ya tenga asociados."
                style={{ marginBottom: 16 }}
            />}
            {!selectedPlanId
                ? <Form.Item name="price" label="Precio acordado" rules={[{ required: true }]}><InputNumber min={0} precision={2} style={{ width: '100%' }} /></Form.Item>
                : <Alert
                    type="info"
                    showIcon
                    message="Precio heredado del convenio"
                    description="El plan no modifica el precio. Solo puede configurar la cobertura y el copago del producto o servicio global del convenio."
                    style={{ marginBottom: 16 }}
                />}
            {selectedPlanId && !isOwnBusiness && <>
                <Form.Item name="coverageType" label="Tipo de cobertura" rules={[{ required: true }]}><Select options={['PORCENTAJE', 'VALOR_FIJO'].map(value => ({ value }))} /></Form.Item>
                <Form.Item name="coverageValue" label="Valor cubierto" rules={[{ required: true }]}><InputNumber min={0} precision={2} style={{ width: '100%' }} /></Form.Item>
                <Form.Item name="copaymentType" label="Tipo de copago" rules={[{ required: true }]}><Select options={['PORCENTAJE', 'VALOR_FIJO'].map(value => ({ value }))} /></Form.Item>
                <Form.Item name="copaymentValue" label="Valor del copago" rules={[{ required: true }]}><InputNumber min={0} precision={2} style={{ width: '100%' }} /></Form.Item>
            </>}
            {!selectedPlanId && !isOwnBusiness && <Alert
                type="info"
                showIcon
                message="Cobertura y copago se configuran en el plan"
                description="El convenio global solo registra el producto o servicio y su precio base. Después, cada plan define la cobertura y el copago, por porcentaje o valor fijo."
                style={{ marginBottom: 16 }}
            />}
            <Form.Item name="description" label="Observación"><Input.TextArea rows={2} /></Form.Item>
        </>;
    };
    const fields = () => {
        switch (kind) {
            case 'business': {
                const isOwn = businessType === 'PROPIA';
                const isNewBusiness = !editing;
                const editingLegacyLocal = !isOwn
                    && String(valueOf(editing || {}, 'sourceType', 'SourceType') || '').toUpperCase() === 'LOCAL';
                const editingCatalogBusinessId = String(valueOf(editing || {}, 'businessId', 'BusinessId') || '');
                const associatedCatalogIds = new Set(
                    businesses
                        .filter(row => String(valueOf(row, 'sourceType', 'SourceType') || '').toUpperCase() === 'CATALOG')
                        .map(row => String(valueOf(row, 'businessId', 'BusinessId') || ''))
                        .filter(Boolean)
                );
                const catalogOptions = catalogue
                    .filter(row => !businessType || String(valueOf(row, 'type', 'Type') || '').toUpperCase() === businessType)
                    .filter(row => !associatedCatalogIds.has(idOf(row)) || idOf(row) === editingCatalogBusinessId)
                    .map(row => ({ value: idOf(row), label: labelOf(row, 'name', 'Name') }));
                const ownName = String(organizationValue('name', 'Name', 'legalName', 'LegalName') || '');
                const ownRuc = String(organizationValue('ruc', 'Ruc') || '');
                const typeOptions = isNewBusiness && !ownBusiness
                    ? ['PROPIA']
                    : isNewBusiness && ownBusinessActive
                        ? ['ASEGURADORA', 'TERCERO']
                        : ['PROPIA', 'ASEGURADORA', 'TERCERO'];
                return <>
                    <Form.Item name="type" label="Tipo" rules={[{ required: true, message: 'Seleccione el tipo de empresa' }]}>
                        <Select disabled={(!editing && !ownBusiness) || (!!editing && !editingLegacyLocal)} placeholder="Seleccione el tipo" onChange={(value) => setBusinessType(value)} options={typeOptions.map(value => ({
                            value,
                        }))} />
                    </Form.Item>
                    {isOwn && <Alert
                        type="info"
                        showIcon
                        message="Empresa propia"
                        description={`Se utilizarán automáticamente los datos de Identity${ownName || ownRuc ? `: ${[ownName, ownRuc && `RUC ${ownRuc}`].filter(Boolean).join(' · ')}` : '.'} No debe registrar información adicional. Se creará un convenio base indefinido y esta empresa permanecerá activa.`}
                        style={{ marginBottom: 16 }}
                    />}
                    {!isOwn && !editingLegacyLocal && <Form.Item name="businessId" label="Empresa del catálogo" rules={[{ required: true, message: 'Seleccione una empresa del catálogo' }]}>
                        <Select disabled={!businessType} showSearch optionFilterProp="label" allowClear options={catalogOptions} />
                    </Form.Item>}
                    {editingLegacyLocal && <Alert
                        type="warning"
                        showIcon
                        message="Asociación local existente"
                        description="Esta asociación fue creada con el modelo anterior. Puede editarla, pero las nuevas asociaciones se seleccionan desde el catálogo global."
                        style={{ marginBottom: 16 }}
                    />}
                    {editingLegacyLocal && <>
                        <Form.Item name="name" label="Nombre local"><Input /></Form.Item>
                        <Form.Item name="ruc" label="RUC"><Input /></Form.Item>
                        <Form.Item name="address" label="Dirección"><Input /></Form.Item>
                        <Form.Item name="phone" label="Teléfono"><Input /></Form.Item>
                        <Form.Item name="emailLegalRepresentative" label="Correo"><Input /></Form.Item>
                    </>}
                    {commonFields(isOwn ? ['ACTIVO'] : ['ACTIVO', 'INACTIVO'])}
                </>;
            }
            case 'agreement': return <><Form.Item name="name" label="Nombre del convenio" rules={[{ required: true, message: 'Ingrese el nombre del convenio' }]}><Input /></Form.Item><Form.Item name="observation" label="Observación"><Input.TextArea rows={2} /></Form.Item><Form.Item name="startDate" label="Fecha de inicio"><Input type="date" /></Form.Item><Form.Item name="endDate" label="Fecha de fin"><Input type="date" /></Form.Item>{commonFields(['BORRADOR', 'ACTIVO', 'VENCIDO', 'CANCELADO'])}</>;
            case 'plan': return <><Form.Item name="name" label="Nombre del plan" rules={[{ required: true }]}><Input /></Form.Item><Form.Item name="observation" label="Observación"><Input.TextArea rows={2} /></Form.Item><Form.Item name="providerCode" label="Código del financiador"><Input /></Form.Item><Form.Item name="providerPlanName" label="Nombre del plan del financiador"><Input /></Form.Item><Form.Item name="providerBusinessName" label="Financiador"><Input /></Form.Item>{commonFields()}</>;
            case 'product': return coverageFields(false);
            case 'service': return coverageFields(true);
            default: return null;
        }
    };

    const businessTable = <Card title="Empresas asociadas" extra={<Button type="primary" icon={<PlusOutlined />} disabled={!canAddBusiness} title={!canAddBusiness ? 'Reactive la empresa PROPIA antes de asociar empresas externas.' : undefined} onClick={() => open('business')}>Agregar empresa</Button>}>
        {ownBusiness && <Alert
            type={ownBusinessActive ? 'info' : 'warning'}
            showIcon
            message={ownBusinessActive ? 'Empresa propia ya registrada' : 'Empresa propia inactiva'}
            description={ownBusinessActive
                ? 'La organización solo puede tener una asociación PROPIA. Desde ahora puede asociar aseguradoras o terceros del catálogo.'
                : 'Reactive la asociación PROPIA existente antes de registrar aseguradoras o terceros.'}
            style={{ marginBottom: 16 }}
        />}
        <Table rowKey={idOf} dataSource={businesses} loading={loading} locale={{ emptyText: <Empty description="No hay empresas asociadas" /> }} columns={[{ title: 'Empresa', render: (_: any, row: Row) => labelOf(row, 'name', 'businessName') || labelOf(row, 'ruc') }, { title: 'Tipo', dataIndex: 'type' }, { title: 'Origen', dataIndex: 'sourceType' }, { title: 'Estado', dataIndex: 'status', render: (v: string) => <Tag color={v === 'ACTIVO' ? 'green' : 'default'}>{v}</Tag> }, { title: 'Acciones', render: (_: any, row: Row) => { const isOwn = String(valueOf(row, 'type', 'Type') || '').toUpperCase() === 'PROPIA'; return <Space wrap><Button type="link" size="small" icon={<SettingOutlined />} onClick={() => manageBusiness(row)}>Gestionar</Button><Button size="small" icon={<EditOutlined />} onClick={() => open('business', row)}>Editar</Button>{isOwn ? <Tag color="blue">Obligatoria</Tag> : <Button size="small" danger icon={<StopOutlined />} onClick={() => void inactivate(row)}>Inactivar</Button>}</Space>; } }]} />
    </Card>;
    const agreementTable = <Card title="Convenios" extra={<Button type="primary" icon={<PlusOutlined />} disabled={!selectedBusinessId} onClick={() => open('agreement')}>Agregar convenio</Button>}><Table rowKey={idOf} dataSource={agreements} locale={{ emptyText: <Empty description="Seleccione una empresa" /> }} columns={[{ title: 'Convenio', render: (_: any, row: Row) => labelOf(row, 'name', 'businessName') || 'Convenio sin nombre' }, { title: 'Inicio', dataIndex: 'startDate' }, { title: 'Fin', dataIndex: 'endDate' }, { title: 'Estado', dataIndex: 'status' }, { title: 'Acciones', render: (_: any, row: Row) => <Space wrap><Button type="link" size="small" icon={<RightOutlined />} onClick={() => manageAgreement(row)}>Gestionar convenio</Button><Button size="small" icon={<EditOutlined />} onClick={() => open('agreement', row)}>Editar</Button></Space> }]} /></Card>;
    const planTable = <Card title="Planes del convenio" extra={<Button type="primary" icon={<PlusOutlined />} disabled={!selectedAgreementId} onClick={() => open('plan')}>Agregar plan</Button>}><Table rowKey={idOf} dataSource={plans} locale={{ emptyText: <Empty description="No hay planes de cobertura" /> }} columns={[{ title: 'Plan', dataIndex: 'name' }, { title: 'Financiador', dataIndex: 'providerBusinessName' }, { title: 'Estado', dataIndex: 'status' }, { title: 'Acciones', render: (_: any, row: Row) => <Space wrap><Button type="link" size="small" icon={<RightOutlined />} onClick={() => managePlan(row)}>Gestionar productos</Button><Button size="small" icon={<EditOutlined />} onClick={() => open('plan', row)}>Editar</Button></Space> }]} /></Card>;
    const productTable = <Card title={selectedBusinessIsOwn ? 'Productos y precios del convenio' : 'Productos, coberturas y copagos del convenio'} extra={<Button icon={<UploadOutlined />} disabled={!selectedPlanId || !selectedBusinessId} onClick={openBulk}>Carga masiva de coberturas</Button>}><Alert type="info" showIcon message="Productos provenientes de la cobertura global" description="Este plan solo puede configurar productos que ya fueron registrados en la cobertura global del convenio. Para agregar otro, regístrelo primero en la cobertura global." style={{ marginBottom: 16 }} /><Table rowKey={idOf} dataSource={planProductRows} locale={{ emptyText: <Empty description="No hay productos en la cobertura global del convenio" /> }} columns={[
        { title: 'Producto', dataIndex: 'productName' },
        { title: 'Precio', dataIndex: 'price', render: (v: number) => `$${Number(v || 0).toFixed(2)}` },
        ...(!selectedBusinessIsOwn ? [
            { title: 'Cobertura', render: (_: any, row: Row) => row.isConfigured ? `${valueOf(row, 'coverageValue') ?? 0} ${valueOf(row, 'coverageType') || ''}` : <Tag color="orange">Pendiente</Tag> },
             { title: 'Copago', render: (_: any, row: Row) => row.isConfigured ? `${valueOf(row, 'copaymentValue') ?? 0} ${valueOf(row, 'copaymentType') || ''}` : <Tag color="orange">Pendiente</Tag> },
             { title: 'A cobrar aseguradora', render: (_: any, row: Row) => row.isConfigured ? `$${Number(valueOf(row, 'insurerAmount', 'InsurerAmount') || 0).toFixed(2)}` : <Tag color="orange">Pendiente</Tag> }
        ] : []),
        { title: 'Acciones', render: (_: any, row: Row) => selectedBusinessIsOwn
            ? <Tag color="blue">Heredado del convenio</Tag>
            : row.isConfigured
                ? <Button size="small" icon={<EditOutlined />} onClick={() => open('product', row)}>Editar cobertura</Button>
                : <Button size="small" type="primary" onClick={() => open('product', row)}>Configurar cobertura</Button> }
    ]} /></Card>;
    const serviceTable = <Card title={selectedBusinessIsOwn ? 'Servicios y precios del convenio' : 'Servicios, coberturas y copagos del convenio'}><Alert type="info" showIcon message="Servicios provenientes de la cobertura global" description="Este plan solo puede configurar servicios que ya fueron registrados en la cobertura global del convenio. Para agregar otro, regístrelo primero en la cobertura global." style={{ marginBottom: 16 }} /><Table rowKey={idOf} dataSource={planServiceRows} locale={{ emptyText: <Empty description="No hay servicios en la cobertura global del convenio" /> }} columns={[
        { title: 'Servicio', dataIndex: 'serviceName' },
        { title: 'Precio', dataIndex: 'price', render: (v: number) => `$${Number(v || 0).toFixed(2)}` },
        ...(!selectedBusinessIsOwn ? [
            { title: 'Cobertura', render: (_: any, row: Row) => row.isConfigured ? `${valueOf(row, 'coverageValue') ?? 0} ${valueOf(row, 'coverageType') || ''}` : <Tag color="orange">Pendiente</Tag> },
             { title: 'Copago', render: (_: any, row: Row) => row.isConfigured ? `${valueOf(row, 'copaymentValue') ?? 0} ${valueOf(row, 'copaymentType') || ''}` : <Tag color="orange">Pendiente</Tag> },
             { title: 'A cobrar aseguradora', render: (_: any, row: Row) => row.isConfigured ? `$${Number(valueOf(row, 'insurerAmount', 'InsurerAmount') || 0).toFixed(2)}` : <Tag color="orange">Pendiente</Tag> }
        ] : []),
        { title: 'Acciones', render: (_: any, row: Row) => selectedBusinessIsOwn
            ? <Tag color="blue">Heredado del convenio</Tag>
            : row.isConfigured
                ? <Button size="small" icon={<EditOutlined />} onClick={() => open('service', row)}>Editar cobertura</Button>
                : <Button size="small" type="primary" onClick={() => open('service', row)}>Configurar cobertura</Button> }
    ]} /></Card>;
    const globalProductServiceOptions = Array.from(new Map(
        services
            .map(row => {
                const serviceId = String(valueOf(row, 'serviceId', 'ServiceId') || '');
                if (!serviceId) return null;
                return [serviceId, { value: serviceId, label: labelOf(row, 'serviceName', 'ServiceName', 'name', 'Name') || serviceId }];
            })
            .filter(Boolean) as Array<[string, { value: string; label: string }]>
    ).values());
    const globalProductTable = <Card title="Productos y precios globales del convenio" extra={<Button type="primary" icon={<PlusOutlined />} disabled={!selectedAgreementId} onClick={() => open('product')}>Agregar producto</Button>}>
        <Space wrap style={{ marginBottom: 16 }}>
            <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Filtrar por servicio"
                value={globalProductServiceId || undefined}
                options={globalProductServiceOptions}
                style={{ minWidth: 260 }}
                onChange={(value) => {
                    setGlobalProductServiceId(String(value || ''));
                    setGlobalProductPage(1);
                }}
            />
            <Input.Search
                allowClear
                placeholder="Buscar por código o producto"
                value={globalProductSearch}
                style={{ width: 320 }}
                onChange={(event) => {
                    if (!event.target.value) {
                        setGlobalProductSearch('');
                        setGlobalProductPage(1);
                    }
                }}
                onSearch={(value) => {
                    setGlobalProductSearch(value);
                    setGlobalProductPage(1);
                }}
            />
        </Space>
        <Table
            rowKey={idOf}
            loading={globalProductLoading}
            dataSource={globalProductCoverage}
            pagination={false}
            locale={{ emptyText: <Empty description="No hay productos globales configurados" /> }}
            columns={[
                { title: 'Producto', dataIndex: 'productName' },
                { title: 'Precio', dataIndex: 'price', render: (v: number) => `$${Number(v || 0).toFixed(2)}` },
                { title: 'Acciones', render: (_: any, row: Row) => <Button size="small" icon={<EditOutlined />} onClick={() => open('product', row)}>Editar</Button> }
            ]}
        />
        <Pagination
            size="small"
            current={globalProductPage}
            pageSize={globalProductPageSize}
            total={globalProductTotal}
            showSizeChanger={false}
            hideOnSinglePage
            onChange={(page) => setGlobalProductPage(page)}
            style={{ marginTop: 16, textAlign: 'right' }}
        />
    </Card>;
    const globalServiceTable = <Card title="Servicios y precios globales del convenio" extra={<Button type="primary" icon={<PlusOutlined />} disabled={!selectedAgreementId} onClick={() => open('service')}>Agregar servicio</Button>}><Table rowKey={idOf} dataSource={globalServiceCoverage} locale={{ emptyText: <Empty description="No hay servicios globales configurados" /> }} columns={[
        { title: 'Servicio', dataIndex: 'serviceName' },
        { title: 'Precio', dataIndex: 'price', render: (v: number) => `$${Number(v || 0).toFixed(2)}` },
        { title: 'Acciones', render: (_: any, row: Row) => <Button size="small" icon={<EditOutlined />} onClick={() => open('service', row)}>Editar</Button> }
    ]} /></Card>;

    const contextCard = (titleText: string, backLabel: string, onBack: () => void, description?: string) => <Card
        size="small"
        title={titleText}
        extra={<Button icon={<ArrowLeftOutlined />} onClick={onBack}>Volver a {backLabel}</Button>}
        style={{ marginBottom: 16 }}
    >
        {description && <Typography.Text type="secondary">{description}</Typography.Text>}
    </Card>;

    const matrixButton = !selectedBusinessIsOwn && selectedBusinessId
        ? <Button type="primary" onClick={goMatrix}>Matriz de precios</Button>
        : null;
    const navigationContent = navigationLevel === 'businesses'
        ? businessTable
        : navigationLevel === 'agreements'
            ? <>{contextCard(
                `Gestión de convenios${selectedBusiness ? ` · ${labelOf(selectedBusiness, 'name', 'businessName')}` : ''}`,
                'empresas',
                goBusinesses,
                'Configure los convenios asociados a esta empresa.'
            )}{matrixButton && <div style={{ marginBottom: 16 }}>{matrixButton}</div>}{agreementTable}</>
            : navigationLevel === 'plans'
                    ? <>{contextCard(
                        `Gestión del convenio${selectedAgreement ? ` · ${labelOf(selectedAgreement, 'name', 'businessName') || 'Convenio seleccionado'}` : ''}`,
                        'convenios',
                        goAgreements,
                        'El convenio contiene directamente sus planes y su cobertura global. Ya no existe una capa intermedia de grupos.'
                    )}{!selectedBusinessIsOwn && <div style={{ marginBottom: 16 }}><Button type="primary" onClick={goMatrix}>Matriz de precios por convenio</Button></div>}<Tabs items={[{ key: 'global-products', label: 'Cobertura global por producto', children: globalProductTable }, { key: 'global-services', label: 'Cobertura global por servicio', children: globalServiceTable }]} />{planTable}</>
                    : <>{contextCard(
                        `Configuración del plan${selectedPlan ? ` · ${labelOf(selectedPlan, 'name')}` : ''}`,
                        'planes',
                        goPlans,
                        selectedBusinessIsOwn
                            ? 'Administre los productos y servicios y defina sus precios base. Cobertura y copago solo aplican a aseguradoras y terceros.'
                            : 'Administre los productos y servicios, precios, coberturas y copagos de este plan.'
                    )}<Tabs activeKey={coverageTab} onChange={setCoverageTab} items={[
                        { key: 'products', label: 'Productos', children: productTable },
                        { key: 'services', label: 'Servicios', children: serviceTable }
                    ]} /></>;
    const matrixContent = navigationLevel === 'matrix' && selectedBusiness
        ? <AgreementPriceMatrix
            organizationId={organizationId}
            businessName={labelOf(selectedBusiness, 'name', 'businessName')}
            agreements={agreements}
            products={products}
            services={services}
            onBack={() => setNavigationLevel('agreements')}
        />
        : null;
    const title = kind === 'business' ? 'empresa asociada' : kind === 'agreement' ? 'convenio' : kind === 'plan' ? 'plan de cobertura' : kind === 'product' ? 'producto' : 'servicio';
    return <Card title="Empresas y coberturas" extra={<Button icon={<ReloadOutlined />} onClick={() => void reloadCurrent()}>Actualizar</Button>}>
        <Alert type="info" showIcon message="Configuración comercial de la organización" description="Asocie empresas y convenios. Cada convenio administra su cobertura global por producto o servicio y, cuando corresponde, planes con excepciones de precio, cobertura y copago." style={{ marginBottom: 16 }} />
        {error && <Alert type="error" showIcon closable onClose={() => setError(null)} message={error} style={{ marginBottom: 16 }} />}
        {loading && !businesses.length ? <Spin /> : (matrixContent || navigationContent)}
        <Modal open={kind !== null} title={`${editing ? 'Editar' : 'Nuevo'} ${title}`} onCancel={close} footer={null} destroyOnClose width={650}>
            <Form form={form} layout="vertical" onFinish={(values) => void save(values)}>{fields()}<Space><Button onClick={close}>Cancelar</Button><Button type="primary" htmlType="submit">Guardar</Button></Space></Form>
        </Modal>
        <Modal
            open={bulkOpen}
            title="Carga masiva de coberturas del plan"
            onCancel={() => setBulkOpen(false)}
            footer={null}
            destroyOnClose
            width={900}
        >
            <Alert
                type={String(valueOf(selectedBusiness || {}, 'type', 'Type') || '').toUpperCase() === 'PROPIA' ? 'info' : 'warning'}
                showIcon
                style={{ marginBottom: 16 }}
                message={String(valueOf(selectedBusiness || {}, 'type', 'Type') || '').toUpperCase() === 'PROPIA' ? 'Lista base de la organización' : 'Lista de convenio externo'}
                 description={String(valueOf(selectedBusiness || {}, 'type', 'Type') || '').toUpperCase() === 'PROPIA'
                     ? 'El plan muestra los productos registrados en la cobertura global del convenio. El precio se hereda del convenio y no se crean productos nuevos.'
                     : 'El archivo solo configura productos que ya estén asociados a la organización y registrados en la cobertura global del convenio. Si falta uno, se rechaza toda la carga y no se guarda ninguna fila.'}
            />
            <Space wrap style={{ marginBottom: 16 }}>
                <Button icon={<DownloadOutlined />} onClick={downloadBulkTemplate}>Descargar plantilla Excel</Button>
                <Button icon={<UploadOutlined />} onClick={() => document.getElementById('business-price-list-file')?.click()}>Seleccionar Excel</Button>
                <input id="business-price-list-file" type="file" accept=".xlsx,.xls,.csv" onChange={(event) => void handleBulkFile(event)} style={{ display: 'none' }} />
                {bulkFileName && <Typography.Text type="secondary">{bulkFileName} · {bulkRows.length} filas</Typography.Text>}
            </Space>
            {bulkRows.length > 0 && <Table
                size="small"
                pagination={{ pageSize: 8 }}
                rowKey={(_, index) => String(index)}
                dataSource={bulkRows}
                scroll={{ x: 850 }}
                columns={[
                    { title: 'Código', render: (_: any, row: Row) => row.productCode || row.productId || '-' },
                    { title: 'Precio', render: (_: any, row: Row) => Number(row.price).toFixed(2) },
                    ...(!selectedBusinessIsOwn ? [
                        { title: 'Cobertura', render: (_: any, row: Row) => `${row.coverageValue} ${row.coverageType}` },
                        { title: 'Copago', render: (_: any, row: Row) => `${row.copaymentValue} ${row.copaymentType}` }
                    ] : []),
                    { title: 'Estado', dataIndex: 'status' }
                ]}
            />}
            {bulkResult && <Alert
                type={bulkResult.success ? 'success' : 'error'}
                showIcon
                style={{ marginTop: 16 }}
                message={bulkResult.success
                    ? `${bulkResult.applied === false ? 'Validación correcta' : 'Carga aplicada'} · ${bulkResult.createdRows || 0} nuevas · ${bulkResult.updatedRows || 0} actualizadas`
                    : 'Carga rechazada: no se guardó ninguna fila'}
                description={<Table
                    size="small"
                    pagination={{ pageSize: 6 }}
                    rowKey="rowNumber"
                    dataSource={Array.isArray(bulkResult.rows) ? bulkResult.rows : []}
                    columns={[{ title: 'Fila', dataIndex: 'rowNumber' }, { title: 'Código', dataIndex: 'productCode' }, { title: 'Resultado', dataIndex: 'status' }, { title: 'Motivo', dataIndex: 'message' }]}
                />}
            />}
            <Space style={{ marginTop: 16 }}>
                <Button onClick={() => setBulkOpen(false)}>Cerrar</Button>
                <Button type="primary" loading={bulkSubmitting} disabled={!bulkRows.length} onClick={() => void submitBulk()}>Validar y guardar</Button>
            </Space>
        </Modal>
    </Card>;
}
